import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TextField, Autocomplete, MenuItem } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { useNavigate } from "react-router-dom";
// css
import "./Projectlisting.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import searchbtn from "../../../Assets/images/search.svg";
import Pagination from "../../Pagination/Pagination";
import deleteimg from "../../../Assets/images/delete-img.png";
// custom imports
import showToast from "../../../Utils/Toast/ToastNotification";
import { HandleApiError } from "../../../Utils/CommonErrors/HandleApiError";
import projectManagementService from "../../../Services/projectManagementService/projectManagementService";
import userManagementService from "../../../Services/UserManagement/UserManagementService";
import {
  showLoader,
  hideLoader,
} from "../../../Redux/Slice/Loader/loaderSlice";

const ProjectListing = () => {
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("startDate");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFilterCleared, setIsFilterCleared] = useState(false);
  const [users, setUsers] = useState([]);
  const [states, setStates] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [filters, setFilters] = useState({
    project_type_id: "",
    status: "",
    start_date: "",
    end_date: "",
    project_coordinator_ids: "",
    page: 1,
    limit: 10,
  });


  const user_id = useSelector(
    (state) => state.auth?.user?.data?.body?.data?.userData?.id
  );

  const role_name = useSelector(
    (state) => state.auth?.user?.data?.body?.data?.role_name
  );
  const token = useSelector(
    (state) => state.auth?.user?.data?.body?.data?.token
  );



  const getProjects = async () => {
    dispatch(showLoader());
    const params = {
      search: searchQuery || "",
      start_date: filters.start_date || "",
      end_date: filters.end_date || "",
      status: filters.status,
      project_coordinator_ids: filters.project_coordinator_ids,
      project_type_id: filters.project_type_id,
      page: page + 1,
      limit: rowsPerPage,
      sort_by: `"${sortBy}"`,
      sort_order: sortOrder,
      is_archived: showArchived ? "" : "false",
    };
    try {
      const response = await projectManagementService.getProjects(params, token);
      if (response?.data?.statusCode === 200) {
        setProjects(response?.data?.body?.data?.projects);
        setTotalRecords(response.data?.body?.data?.total || 0);
      } else {
        showToast("error", response.data.body?.message);
      }
    } catch (error) {
      await HandleApiError(error);
    } finally {
      dispatch(hideLoader()); // Hide loader
    }
  };
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(e.target.value);
    setPage(0);
  };

  useEffect(() => {
    getProjects();
    getStates();
    getProjectTypes();
    getUser();
  }, [debouncedSearch, page, rowsPerPage, sortBy, sortOrder]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const handleClearFilter = (e) => {
    e.preventDefault();
    // Reset filter state
    setFilters({
      project_type_id: "",
      status: "",
      start_date: "",
      end_date: "",
      project_coordinator_ids: "",
      page: page + 1,
      limit: rowsPerPage,
    });
    setIsFilterCleared(true);
    setSearchQuery("");
    if (startDateRef.current) startDateRef.current.value = "";
    if (endDateRef.current) endDateRef.current.value = "";
    // getProjects();
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    getProjects();
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSortBy(column);
      setSortOrder("ASC");
    }
  };

  const handleFocus = (ref) => {
    if (ref.current) {
      ref.current.showPicker?.();
    }
  };

  const getStates = async () => {
    try {
      const response = await projectManagementService.getStates(token);
      if (response?.data?.statusCode === 200) {
        setStates(response.data.body.data.states);
      } else {
        showToast("error", response.data.body.message);
      }
    } catch (error) {
      await HandleApiError(error);
    }
  };

  const getProjectTypes = async () => {
    try {
      const response = await projectManagementService.getProjectTypes(token);
      if (response?.data?.statusCode === 200) {
        setProjectTypes(response.data.body.data.states);
      } else {
        showToast("error", response.data.body.message);
      }
    } catch (error) {
      await HandleApiError(error);
    }
  };

  const handleDelete = async () => {
    try {
      showLoader(true);
      const params = {
        id: selectedProjectId,
        deleted_by: user_id,
      };
      await projectManagementService.deleteProject(params, token);

      showToast("success", "Project deleted successfully");
      await getProjects();
    } catch (error) {
      await HandleApiError(error);
    } finally {
      showLoader(false);
    }
  };

  const handleArchive = async () => {
    try {
      showLoader(true);
      const params = {
        id: selectedProjectId,
        deleted_by: user_id,
      };
      await projectManagementService.archiveProject(params, token);

      showToast("success", "Project archived successfully");
      await getProjects();
    } catch (error) {
      await HandleApiError(error);
    } finally {
      showLoader(false);
    }
  };
  const getUser = async () => {
    try {
      const params = {
        search: searchQuery || "",
        limit: 100,
      };
      const response = await userManagementService.getUserList(params, token);
      if (response?.data?.statusCode === 200) {
        setUsers(response.data.body.data.users);
      } else {
        showToast("error", response.data.body.message);
      }
    } catch (error) {
      await HandleApiError(error);
    }
  };

  const handleCoordinatorChange = (event, newValue) => {
    const selectedCoordinators = newValue.map((user) => user.user_id).join(",");
    setFilters((prevFilters) => ({
      ...prevFilters,
      project_coordinator_ids: selectedCoordinators,
    }));
  };
  useEffect(() => {
    if (isFilterCleared) {
      getProjects();
      setIsFilterCleared(false);
    }
  }, [isFilterCleared]);

  useEffect(() => {
    getProjects();
  }, [showArchived]);

  return (

    <div className="main-inner-block">
      <div className="content">
        <div className="d-flex align-items-center justify-content-between mb-4 block-one gap-3">
          <div className="heading-content ms-0 mt-0">Project List</div>
          <div className="heading-content mt-0">
            <button
              className="green-btn green-signin addnewuser"
              onClick={() => navigate("/add-project")}
            >
              + Add Project
            </button>
          </div>
        </div>
        <div className="white-box bg-box-mob">
          <div className="inner-content-block">
            <div className="listing-title-block d-flex justify-content-between">
              <div className="box-listing-title">
                Projects List
                <span className="d-block">
                  Showing a list of Projects List.
                </span>
              </div>


              <div className="searchbar-btn d-flex align-items-center gap-4">
                <div className="toggle-archive d-flex align-items-center">
                  <label
                    className="form-check-label me-2"
                    htmlFor="archiveToggle"
                  >
                    Show Archived
                  </label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="archiveToggle"
                      checked={showArchived}
                      onChange={(e) => setShowArchived(e.target.checked)}
                    />
                  </div>
                </div>
                <div className="input-search-box position-relative">
                  <input
                    type="text"
                    id="name"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                  <div className="searchbtn">
                    <img src={searchbtn} alt="searchbtn"></img>
                  </div>
                  {searchQuery && (
                    <button
                      className="clear-btn"
                      onClick={() => setSearchQuery("")}
                    >
                      ✖
                    </button>
                  )}
                </div>
                <div className="filter d-flex align-items-center gap-4">
                  <a
                    className="filter-inner"
                    href="#"
                    data-bs-toggle="modal"
                    data-bs-target="#exampleModal"
                  >
                    <span className="me-2">
                      <svg
                        width="20"
                        height="21"
                        viewBox="0 0 20 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 10.332H15M2.5 5.33203H17.5M7.5 15.332H12.5"
                          stroke="#475467"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                    Filter
                  </a>
                  <div
                    class="modal fade"
                    id="exampleModal"
                    tabindex="-1"
                    aria-labelledby="exampleModalLabel"
                    aria-hidden="true"
                  >
                    <div class="modal-dialog modal-dialog-centered">
                      <div class="modal-content">
                        <div className="text-end">
                          <button
                            type="button"
                            class="btn-close "
                            data-bs-dismiss="modal"
                            aria-label="Close"
                          ></button>
                        </div>

                        <div>
                          <h1
                            class="modal-title fs-5 text-center filter-title"
                            id="exampleModalLabel"
                          >
                            Project Filter
                          </h1>
                        </div>
                        <div class="modal-body">
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              getProjects();
                            }}
                          >
                            <div class="mb-3">
                              <label for="ProjectType" class="form-label">
                                Project Type
                              </label>
                              <select
                                class="form-select"
                                id="ProjectType"
                                name="project_type_id"
                                value={filters.project_type_id}
                                onChange={handleFilterChange}
                              >
                                <option value="" disabled>
                                  Select Project Type
                                </option>
                                {Array.isArray(projectTypes) &&
                                  projectTypes.length > 0 ? (
                                  projectTypes.map((projectTyepe_id) => (
                                    <option
                                      key={projectTyepe_id.id}
                                      value={projectTyepe_id.id}
                                    >
                                      {projectTyepe_id.name}
                                    </option>
                                  ))
                                ) : (
                                  <option disabled>
                                    No Project Types available
                                  </option>
                                )}
                              </select>
                            </div>
                            <div class="mb-3">
                              <label
                                for="project_coordinator_ids"
                                class="form-label"
                              >
                                Co-ordinator
                              </label>
                              <Autocomplete
                                className="autocomplete-box position-relative"
                                name="project_coordinator_ids"
                                multiple
                                options={users}
                                getOptionLabel={(option) =>
                                  `${option.first_name} ${option.last_name}`
                                }
                                disableCloseOnSelect
                                value={users.filter((user) =>
                                  filters.project_coordinator_ids.includes(
                                    user.user_id
                                  )
                                )} // FIX: Ensure value is an array of objects
                                onChange={handleCoordinatorChange}
                                sx={{
                                  m: 1,
                                  // width: 375,
                                  "& .MuiOutlinedInput-root": {
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      border: "1px solid #e6e6e6 !important",
                                    },
                                    "&:before, &:after": {
                                      border: "1px solid #e6e6e6 !important",
                                    },
                                    "&.Mui-focused:after": {
                                      border: "1px solid #e6e6e6 !important",
                                    },
                                  },
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    className="coordinator"
                                    placeholder={
                                      filters.project_coordinator_ids && filters.project_coordinator_ids.length > 0
                                        ? ""
                                        : "Co-ordinator"
                                    }
                                  />
                                )}
                                renderOption={(
                                  props,
                                  option,
                                  { selected }
                                ) => (
                                  <MenuItem
                                    {...props}
                                    key={option.user_id}
                                    sx={{ justifyContent: "space-between" }}
                                  >
                                    {`${option.first_name} ${option.last_name}`}
                                    {selected ? (
                                      <CheckIcon color="info" />
                                    ) : null}
                                  </MenuItem>
                                )}
                              />
                            </div>

                            <div class="mb-3">
                              <label for="status" class="form-label">
                                Status
                              </label>
                              <select
                                class="form-select"
                                id="status"
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                              >
                                <option value="" disabled>
                                  Select Status
                                </option>
                                <option selected value="New">
                                  New
                                </option>
                                <option value="Active">Active</option>
                                <option value="Complete">Complete</option>
                                <option value="Inactive">Inactive</option>
                                <option value="On Hold">On Hold</option>
                                <option value="Archive">Archive</option>
                              </select>
                            </div>

                            <div class="mb-3">
                              <label
                                htmlFor="startDate"
                                className="form-label"
                              >
                                Start Date
                              </label>
                              <input
                                type="date"
                                className="form-control"
                                id="startDate"
                                name="start_date"
                                ref={startDateRef}
                                onClick={() => handleFocus(startDateRef)}
                                onChange={handleFilterChange}
                              />
                            </div>
                            <div class="mb-3">
                              <label htmlFor="endDate" className="form-label">
                                End Date
                              </label>
                              <input
                                type="date"
                                className="form-control"
                                id="endDate"
                                name="end_date"
                                ref={endDateRef}
                                onClick={() => handleFocus(endDateRef)}
                                onChange={handleFilterChange}
                              />
                            </div>

                            <div className="text-center">
                              <button
                                type="submit"
                                class="green-btn updt-button"
                                data-bs-dismiss="modal"
                                onClick={(e) => handleFilterSubmit(e)}
                              >
                                Filter
                              </button>
                              <button
                                type="submit"
                                class="green-btn updt-button ms-2"
                                data-bs-dismiss="modal"
                                onClick={handleClearFilter}
                              >
                                Clear Filter
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="table-block d-none d-md-block">
              <table class="table">
                <thead class="table-headers">
                  <tr>
                    {/* <th>Sr.no.</th> */}
                    <th>Project Type</th>
                    <th onClick={() => handleSort("streetAddress")}>
                      Project address
                      <span className="ms-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          version="1.1"
                          width="10"
                          height="17"
                          viewBox="0 0 32 32"
                        >
                          <g>
                            <path
                              d="M7 13h18a1 1 0 0 0 .707-1.707l-9-9a1 1 0 0 0-1.414 0l-9 9A1 1 0 0 0 7 13zM25 19H7a1 1 0 0 0-.707 1.707l9 9a1 1 0 0 0 1.414 0l9-9A1 1 0 0 0 25 19z"
                              fill="#949494"
                              opacity="1"
                            />
                          </g>
                        </svg>
                      </span>
                    </th>
                    <th>Coordinator</th>
                    <th>No. of Inspection</th>
                    <th onClick={() => handleSort("startDate")}>
                      Start date
                      <span className="ms-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          version="1.1"
                          width="10"
                          height="17"
                          viewBox="0 0 32 32"
                        >
                          <g>
                            <path
                              d="M7 13h18a1 1 0 0 0 .707-1.707l-9-9a1 1 0 0 0-1.414 0l-9 9A1 1 0 0 0 7 13zM25 19H7a1 1 0 0 0-.707 1.707l9 9a1 1 0 0 0 1.414 0l9-9A1 1 0 0 0 25 19z"
                              fill="#949494"
                              opacity="1"
                            />
                          </g>
                        </svg>
                      </span>
                    </th>
                    <th onClick={() => handleSort("endDate")}>
                      End date
                      <span className="ms-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          version="1.1"
                          width="10"
                          height="17"
                          viewBox="0 0 32 32"
                        >
                          <g>
                            <path
                              d="M7 13h18a1 1 0 0 0 .707-1.707l-9-9a1 1 0 0 0-1.414 0l-9 9A1 1 0 0 0 7 13zM25 19H7a1 1 0 0 0-.707 1.707l9 9a1 1 0 0 0 1.414 0l9-9A1 1 0 0 0 25 19z"
                              fill="#949494"
                              opacity="1"
                            />
                          </g>
                        </svg>
                      </span>
                    </th>
                    <th onClick={() => handleSort("status")}>
                      Status
                      <span className="ms-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          version="1.1"
                          width="10"
                          height="17"
                          viewBox="0 0 32 32"
                        >
                          <g>
                            <path
                              d="M7 13h18a1 1 0 0 0 .707-1.707l-9-9a1 1 0 0 0-1.414 0l-9 9A1 1 0 0 0 7 13zM25 19H7a1 1 0 0 0-.707 1.707l9 9a1 1 0 0 0 1.414 0l9-9A1 1 0 0 0 25 19z"
                              fill="#949494"
                              opacity="1"
                            />
                          </g>
                        </svg>
                      </span>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.length > 0 ? (
                    projects.map((projects, index) => (
                      <tr key={projects.id}>
                        {/* <td>{index + 1}</td> */}
                        <td>{projects.project_type_name}</td>
                        <td>
                          <a
                            className="name-email-text"
                            onClick={() =>
                              navigate(`/view-project/${projects.id}`)
                            }
                            style={{
                              textDecoration: "none",
                              color: "inherit",
                            }}
                          >
                            {projects.streetAddress} {projects.suburb}{" "}
                            {projects.state_name} {projects.postCode}
                          </a>
                        </td>
                        <td>
                          {projects.coordinators.length > 0
                            ? projects.coordinators
                              .map(
                                (coordinator) =>
                                  `${coordinator.first_name} ${coordinator.last_name}`
                              )
                              .join(", ")
                            : "No Coordinators"}
                        </td>
                        <td style={{
                          textAlign: !projects.inspection_count ? "center" : "left",
                        }}
                        >
                          {projects.inspection_count ? projects.inspection_count : "-"}
                        </td>
                        <td>
                          {new Date(projects.startDate).toLocaleDateString()}
                        </td>

                        <td
                          style={{
                            textAlign: !projects.endDate ? "center" : "left",
                          }}
                        >
                          {projects.endDate
                            ? new Date(projects.endDate).toLocaleDateString()
                            : "-"}
                        </td>
                        <td>{projects.status}</td>
                        <td>
                          <div className="d-flex gap-3">
                            <a
                              className="tooltip-container d-flex align-items-center text-decoration-none fonticon"
                              onClick={() =>
                                navigate(`/view-project/${projects.id}`)
                              }
                            >
                              <i className="fas fa-eye me-2"></i>
                              <span className="tooltip-text">View</span>
                            </a>
                            <a
                              className="tooltip-container d-flex align-items-center text-decoration-none fonticon"
                              onClick={() =>
                                navigate(`/update-project/${projects.id}`)
                              }
                            >
                              <i className="fas fa-pencil-alt me-2"></i>
                              <span className="tooltip-text">Update</span>
                            </a>
                            <a
                              className="tooltip-container d-flex align-items-center text-decoration-none fonticon"
                              href="#"
                              data-bs-toggle="modal"
                              data-bs-target="#deletemodal"
                              onClick={() =>
                                setSelectedProjectId(projects.id)
                              }
                            >
                              <i className="fas fa-trash me-2"></i>
                              <span className="tooltip-text">Delete</span>
                            </a>
                            <a
                              className="tooltip-container d-flex align-items-center text-decoration-none fonticon"
                              href="#"
                              data-bs-toggle="modal"
                              data-bs-target="#archiveemodal"
                              onClick={() =>
                                setSelectedProjectId(projects.id)
                              }
                            >
                              <i class="fa fa-archive" aria-hidden="true"></i>
                              <span className="tooltip-text">Archive</span>
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No product found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* responsive */}
            <div className="mobile-user-list d-block d-md-none">
              {projects.length > 0 ? (
                projects.map((project, index) => (
                  <div key={project.id} className="col-md-6 col-lg-4 mb-4">
                    <div className="card shadow-sm h-100">
                      <div className="card-body">


                        <p className="mb-1">
                          <strong>Project Type:</strong>{" "}
                          {project.project_type_name}
                        </p>

                        <p className="mb-1">
                          <strong>Address: </strong>
                          <a
                            className="name-email-text"
                            style={{
                              textDecoration: "none",
                              color: "inherit",
                            }}
                            onClick={() =>
                              navigate(`/view-project/${project.id}`)
                            }
                          >
                            {project.streetAddress} {project.suburb}{" "}
                            {project.state_name} {project.postCode}
                          </a>
                        </p>

                        <p className="mb-1">
                          <strong>Coordinator:</strong>{" "}
                          {project.coordinators.length > 0
                            ? project.coordinators
                              .map(
                                (coordinator) =>
                                  `${coordinator.first_name} ${coordinator.last_name}`
                              )
                              .join(", ")
                            : "No Coordinators"}
                        </p>

                        <p className="mb-1">
                          <strong>Inspections:</strong>
                          {project.inspection_count}
                        </p>

                        <p className="mb-1">
                          <strong>Start Date:</strong>{" "}
                          {new Date(project.startDate).toLocaleDateString()}
                        </p>

                        <p className="mb-1">
                          <strong>End Date:</strong>{" "}
                          {project.endDate
                            ? new Date(project.endDate).toLocaleDateString()
                            : "-"}
                        </p>

                        <p className="mb-1">
                          <strong>Status:</strong>{" "}
                          {project.status}
                        </p>

                        <div className="d-flex justify-content-end mt-3 gap-3">
                          <a
                            className="text-decoration-none fonticon"
                            onClick={() =>
                              navigate(`/view-project/${project.id}`)
                            }
                          >
                            <i className="fas fa-eye me-2"></i>
                          </a>
                          <a
                            className="text-decoration-none fonticon"
                            onClick={() =>
                              navigate(`/update-project/${project.id}`)
                            }
                          >
                            <i className="fas fa-pencil-alt me-2"></i>
                          </a>
                          <a
                            className="text-decoration-none fonticon"
                            href="#"
                            data-bs-toggle="modal"
                            data-bs-target="#deletemodal"
                            onClick={() => setSelectedProjectId(project.id)}
                          >
                            <i className="fas fa-trash me-2"></i>
                          </a>
                          <a
                            className="d-flex align-items-center text-decoration-none fonticon"
                            href="#"
                            data-bs-toggle="modal"
                            data-bs-target="#archiveemodal"
                            onClick={() =>
                              setSelectedProjectId(project.id)
                            }
                          >
                            <i class="fa fa-archive" aria-hidden="true"></i>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-12 text-center">
                  <div className="alert alert-info">No product found</div>
                </div>
              )}
            </div>

            <div className="pagination-block">
              {
                <Pagination
                  count={totalRecords}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={handleChangePage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              }
            </div>
          </div>
        </div>

        <div
          class="modal fade"
          id="deletemodal"
          tabindex="-1"
          aria-labelledby="deletemodalLabel"
          aria-hidden="true"
        >
          <div class="modal-dialog modal-dialog-centered delete-modal">
            <div class="modal-content">
              <div class="modal-body">
                <div className="text-center delete-img-blk">
                  <img src={deleteimg} alt="delete-img"></img>
                </div>
                <div className="ques-title text-center">
                  Are you sure ?
                  <p className="m-0">
                    Are you sure you really want to delete this record? This
                    process cannot be undone.
                  </p>
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-center gap-3 btn-block">
                <button
                  type="button"
                  class="gray-outline-btn"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="orange-btn"
                  data-bs-dismiss="modal"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          class="modal fade"
          id="archiveemodal"
          tabindex="-1"
          aria-labelledby="archiveemodalLabel"
          aria-hidden="true"
        >
          <div class="modal-dialog modal-dialog-centered delete-modal">
            <div class="modal-content">
              <div class="modal-body">
                <div className="text-center delete-img-blk">
                  <img src={deleteimg} alt="delete-img"></img>
                </div>
                <div className="ques-title text-center">
                  Are you sure ?
                  <p className="m-0">
                    Are you sure you really want to archive this record?
                  </p>
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-center gap-3 btn-block">
                <button
                  type="button"
                  class="gray-outline-btn"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="orange-btn"
                  data-bs-dismiss="modal"
                  onClick={handleArchive}
                >
                  Archive
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  );
};

export default ProjectListing;
